import { useMemo, useContext, useState, useEffect, createContext } from 'react';
import { gql, useQuery, useMutation, useSubscription } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { dateTimeFormat } from 'pages/Activity';
import { ActivityCard } from 'components/ActivityList';

import 'styles/ActivityChat.css';
import { DataContext } from 'providers/DataProvider';
import { ALL_PICTURE_FIELDS } from 'graphql/APICalls';

const ACTIVITY_CHAT_QUERY = gql`
  query ($id: ID, $shortId: String) {
    getActivity(id: $id, shortId: $shortId) {
      title, id, shortId, creator { name },
      location { name }, participants { username, picture ${ALL_PICTURE_FIELDS} }
      chat {
        id,
        events {
          id, kind, createdAt,
          ... on MessageEvent { user { username, name }, message }
        }
      }
    }
  }
`;

const NEW_CHAT_EVENT_SUBSCRIPTION = gql`
  subscription ($chatId: ID!) {
    newChatEvent(chatId: $chatId) {
      id, kind, createdAt,
      ... on MessageEvent { user { username, name }, message }
    }
  }
`;

const SEND_MESSAGE_MUTATION = gql`
  mutation ($activityId: ID!, $message: String!) {
    addMessageToActivityChat(activityId: $activityId, message: $message)
  }
`;

const ChatContext = createContext({});

export default function ActivityChat() {

  const { activityId, activityShortId } = useParams();

  const [chatEvents, setChatEvents] = useState([]);

  const activityChatQuery = useQuery(ACTIVITY_CHAT_QUERY, {variables: {id: activityId, shortId: activityShortId}});

  const activity = useMemo(() =>
    activityChatQuery.data?.getActivity
  , [activityChatQuery]);

  // TODO: update userPictures when a new user joins the activity
  const userPictures = useMemo(() => {
    if (!activity) return new Map();
    const pictureMap = new Map();
    activity.participants.forEach(p => pictureMap.set(p.username, p.picture.imageSrc));
    return pictureMap
  }, [activity]);

  useEffect(() => {
    if (!activity?.chat?.events) return;
    setChatEvents(activity.chat.events)
  }, [activity]);

  const { me } = useContext(DataContext);

  const [userMessage, setUserMessage] = useState();

  const [sendSendMessageMutation, sendMessageMutation] = useMutation(SEND_MESSAGE_MUTATION);

  const handleMessageSend = e => {
    e.preventDefault();
    sendSendMessageMutation({
      variables: {activityId: activity.id, username: me.username, message: userMessage}
    });
    setUserMessage('');
  };

  const newChatEventSubscription = useSubscription(NEW_CHAT_EVENT_SUBSCRIPTION, {
    variables: {chatId: activity?.chat?.id}
  });
  
  useEffect(() => {
    if (!newChatEventSubscription.data) return;
    setChatEvents(prev => [...prev, newChatEventSubscription.data.newChatEvent])
  }, [newChatEventSubscription]);

  return (
    <ChatContext.Provider value={{userPictures}}>
      <div className="centerer chat-overlay">
        <div className="centered chat-module">
          <ActivityCard activity={activity}/>
          <div className="chat-display-area">
            {
              // TODO: load messages dynamically when the user scrolls up instead of entire chat history in one go
              (()=>{
                let prevEvent;
                return chatEvents.map(event => {
                  const component = <ChatEvent event={event} prevEvent={prevEvent} key={event.id}/>;
                  prevEvent = event;
                  return component
                }).reverse()
              })()
            }
          </div>
          <form className="chat-input-area" onSubmit={handleMessageSend}>
            <div>
              <input
                type="text"
                value={userMessage}
                autoCapitalize="sentences"
                autoComplete="off"
                inputMode="text"
                placeholder="Message"
                onChange={e=>setUserMessage(e.target.value)}
              />
              <button><div/></button>
            </div>
          </form>
        </div>
      </div>
    </ChatContext.Provider>
  )
}

function ChatEvent({event, prevEvent}){
  return (
    event.kind === 'MessageEvent' ? <MessageEvent event={event} prevEvent={prevEvent}/> : null
  )
}

function MessageEvent({event, prevEvent}){

  const followup =
    !!prevEvent
    && prevEvent.kind==='MessageEvent'
    && prevEvent.user.username === event.user.username
    && (new Date(event.createdAt)) - (new Date(prevEvent.createdAt)) < 300000;

  const { userPictures } = useContext(ChatContext);

  return (
    <div className={"user-message " + (followup ? "followup" : "")}>
      { followup || <>
        <div className="picture-area">
          <div style={{backgroundImage: `url(${userPictures.get(event.user.username)})`}}/>
        </div>
        <div className="username-area"><label>{event.user.name}</label></div>
        <div className="time-area"><label>{dateTimeFormat.format(new Date(event.createdAt))}</label></div>
      </> }
      <div className="message-area">{event.message}</div>
    </div>
  )
}